<script>
  // The inventory reconciliation wizard (spec §6): Scope → Scan → Review →
  // Confirm. Every actionable row carries an explicit adjust/skip decision —
  // doing nothing never writes anything. Nav lives in the page under each
  // step's content, not in a fixed bar.
  //
  // Review is an accordion of accent-bordered group cards. Each card, and each
  // nested miss subgroup, auto-collapses the moment its last row is decided and
  // gains a check plus a done tally (the "done trail"); undecided rows read
  // amber everywhere but never block the confirm.
  //
  // Everything mounts collapsed (spec-addendum §D): the one-shot mount of a
  // 1,400-row review froze the tab for 15–50 s on a fast machine and minutes on
  // shop hardware, ~72% of it layout/paint + Font Awesome icon swapping, with no
  // frame in between to show feedback (artifacts/stall-diagnosis.md). Collapsed
  // cards mount headers only, so the work follows the operator one subgroup at a
  // time. Headers, tallies, toolbars and the transfer banner therefore live
  // *outside* the collapse — every bulk action works without mounting a row.
  import { onMount, tick } from "svelte";
  import {
    result,
    changes,
    decisions,
    overrides,
    missAction,
    entries,
    scope,
    fleet,
    branchNames,
    transfers,
    bulkTransfers,
    ensureFleet,
    resetWizard,
    setOverride,
    decide,
    toggleTransfer,
    transferAll,
    undoBulkTransfer,
    pickReview,
    confirmSummary,
    confirmReconciliation,
  } from "./store";
  import {
    missSubgroups,
    decisionCounts,
    statusLabel,
    missActionStatus,
    isTransferable,
  } from "./engine";
  import ScopePicker from "./ScopePicker.svelte";
  import PasteBox from "./PasteBox.svelte";
  import StatusTag from "./StatusTag.svelte";
  import DecisionControl from "./DecisionControl.svelte";
  import TransferBanner from "./TransferBanner.svelte";
  import TransferToggle from "./TransferToggle.svelte";
  import NeedsReviewPicker from "./NeedsReviewPicker.svelte";
  import Tally from "./Tally.svelte";

  export let branches = [];
  export let unitTypes = [];
  export let missActions = [];
  export let scopeUnitsUrl;
  export let createUrl; // the confirm endpoint (spec §7)

  const STEPS = ["Scope", "Scan", "Review", "Confirm"];
  const REVIEW = 2;
  let step = 0;
  // collapsed by default, no size threshold, every branch (spec-addendum §D)
  let open = { needsReview: false, reactivations: false, misses: false, outsideScope: false };
  let subOpen = {}; // subgroup id -> bool; absent = collapsed

  // Turbo keeps this module alive between visits, so a fresh mount starts from
  // an empty count rather than inheriting the last one. Nothing fetches until
  // that reset has happened.
  let mounted = false;
  onMount(() => {
    resetWizard();
    branchNames.set(Object.fromEntries(branches.map((b) => [b.id, b.name])));
    mounted = true;
  });

  // The fleet dump is company-wide (the engine matches barcodes globally and
  // builds the "belongs elsewhere" group from cross-scope hits), so it is
  // fetched once and re-scoped client-side — changing the scope never refetches.
  $: if (mounted && $scope.branchId && $scope.type) ensureFleet(scopeUnitsUrl, $scope);

  $: scopeChosen = Boolean($scope.branchId && $scope.type);
  $: scopeReady = scopeChosen && $fleet.status === "ready";

  $: groups = $result.groups;
  $: stats = $result.stats;
  $: subs = missSubgroups(groups.misses);
  $: summary = $confirmSummary;
  $: missTarget = missActionStatus($missAction);
  $: reactCounts = decisionCounts(groups.reactivations, $decisions);
  $: missCounts = decisionCounts(groups.misses, $decisions);
  $: outsideCounts = decisionCounts(groups.outsideScope, $decisions, $transfers);

  // ---- outside-scope transfers (spec-addendum §C) -------------------------
  // Eligibility is branch, not type: a wrong-type unit already homed here has
  // nothing to move, so it keeps the classic status-only control.
  $: outsideEligible = groups.outsideScope.filter((row) => isTransferable(row.unit, $scope));
  $: bulkApplied = Object.keys($bulkTransfers).length > 0;
  $: scopeBranchName = $branchNames[$scope.branchId] || "this branch";
  // Outside-scope undecided rows count too — the done trail asks for every row
  // to be decided, so the footer and confirm tallies have to agree.
  $: undecidedTotal = reactCounts.undecided + missCounts.undecided + outsideCounts.undecided;
  $: skipTotal = reactCounts.skip + missCounts.skip + outsideCounts.skip;
  $: nrPending = groups.needsReview.length;
  $: nrResolved = groups.reviewResolved.length;
  // keep the nav hugging the step's content width
  $: navWidth = ["40rem", "40rem", null, "44rem"][step];

  // miss subgroups as one uniform list: incoming / never-barcoded / per-item
  $: missSubList = [
    ...(subs.incoming.length
      ? [{ id: "incoming", title: "Incoming", badgeClass: "bg-info", info: true, hint: "may not be on the rack yet", rows: subs.incoming }]
      : []),
    ...(subs.neverBarcoded.length
      ? [{ id: "never", title: "Never Barcoded", badgeClass: "bg-secondary", hint: "no barcode to scan — count these by SKU", rows: subs.neverBarcoded }]
      : []),
    ...subs.items.map((it) => ({ id: `item:${it.name}`, title: it.name, badgeClass: "bg-secondary", itemGroup: true, rows: it.rows })),
  ].map((sg) => ({ ...sg, counts: decisionCounts(sg.rows, $decisions) }));

  // ---- done-trail ---------------------------------------------------------
  // A card auto-collapses the moment it becomes fully decided, and auto-reopens
  // if new undecided rows land in it (e.g. after a needs-review pick). Manual
  // toggling always works; we only act on transitions.
  let prevDone = {};
  $: doneStates = Object.fromEntries([
    ["g:needsReview", nrResolved > 0 && nrPending === 0],
    ["g:reactivations", groups.reactivations.length > 0 && reactCounts.undecided === 0],
    ["g:misses", groups.misses.length > 0 && missCounts.undecided === 0],
    ["g:outsideScope", groups.outsideScope.length > 0 && outsideCounts.undecided === 0],
    ...missSubList.map((sg) => [`s:${sg.id}`, sg.counts.undecided === 0]),
  ]);
  $: applyDoneTrail(doneStates);

  function applyDoneTrail(states) {
    let gChanged = false;
    let sChanged = false;
    for (const [key, done] of Object.entries(states)) {
      if (done === !!prevDone[key]) continue;
      prevDone[key] = done;
      const id = key.slice(2);
      if (key.startsWith("g:")) {
        open[id] = !done;
        gChanged = true;
      } else {
        subOpen[id] = !done;
        sChanged = true;
      }
    }
    if (gChanged) open = open;
    if (sChanged) subOpen = subOpen;
  }

  // "N set to adjust · N kept as is · N undecided" — zero segments hidden
  const tallyParts = (c) =>
    [
      c.adjust > 0 && { text: `${c.adjust} set to adjust`, kind: "good" },
      c.skip > 0 && { text: `${c.skip} kept as is`, kind: "muted" },
      c.undecided > 0 && { text: `${c.undecided} undecided`, kind: "warn" },
    ].filter(Boolean);
  $: nrParts = [
    nrResolved > 0 && { text: `${nrResolved} resolved`, kind: "good" },
    nrPending > 0 && { text: `${nrPending} to review`, kind: "warn" },
  ].filter(Boolean);
  // The outside-scope card counts a second axis, and says it shorter:
  // "N to transfer · N set · N kept · N undecided" (spec-addendum §C).
  $: outsideParts = [
    outsideCounts.transfer > 0 && { text: `${outsideCounts.transfer} to transfer`, kind: "info" },
    outsideCounts.adjust > 0 && { text: `${outsideCounts.adjust} set`, kind: "good" },
    outsideCounts.skip > 0 && { text: `${outsideCounts.skip} kept`, kind: "muted" },
    outsideCounts.undecided > 0 && { text: `${outsideCounts.undecided} undecided`, kind: "warn" },
  ].filter(Boolean);

  // ---- confirm ------------------------------------------------------------
  // Nothing has been persisted up to this point; this is the single POST that
  // writes the whole count (spec §7). A failure keeps every decision on screen
  // so the operator can fix what the server complained about and try again.
  let applying = false;
  let confirmErrors = [];

  async function apply() {
    if (applying) return;
    applying = true;
    confirmErrors = [];

    const outcome = await confirmReconciliation(createUrl);
    if (outcome.ok) {
      // Straight to the reconciliation, whose flash reports what was applied —
      // including any stale units the server refused to touch (spec §7). The
      // button stays disabled while the browser navigates.
      window.location.assign(outcome.url);
      return;
    }

    confirmErrors = outcome.errors;
    applying = false;
  }

  const toggle = (key) => (open[key] = !open[key]);
  const toggleSub = (id) => (subOpen = { ...subOpen, [id]: !subOpen[id] });
  const ids = (rows) => rows.map((r) => r.unit.id);

  // ---- step nav -----------------------------------------------------------
  // Building the review is still the most expensive thing the wizard does, and
  // it happens in one synchronous Svelte flush, so a spinner rendered in the
  // same tick would never paint. Hand the browser a frame first: flush the
  // spinner (tick), let it actually reach the screen (double rAF), and only
  // then mount the review. Always — a Back→Next remount costs the same as the
  // first one (spec-addendum §D).
  let building = false;
  let navToken = 0;
  const painted = () =>
    new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  async function goToStep(target) {
    if (target === step || target < 0 || target > STEPS.length - 1) return;
    const token = ++navToken;

    if (target !== REVIEW) {
      building = false;
      step = target;
      return;
    }

    building = true;
    step = target;
    await tick();
    await painted();
    if (token !== navToken) return; // navigated away mid-frame
    building = false;
  }
</script>

<div class="pb-4">
  <!-- stepper -->
  <div class="d-flex align-items-center gap-2 mb-4">
    {#each STEPS as label, i}
      <button
        class="btn btn-sm rounded-pill px-3"
        class:btn-primary={i === step}
        class:btn-outline-secondary={i !== step}
        disabled={(i > 0 && !scopeReady) || (i > 1 && $entries.length === 0)}
        on:click={() => goToStep(i)}
      >
        {i + 1}. {label}
        {#if label === "Review" && $entries.length > 0}
          <span class="badge bg-light text-dark ms-1">{stats.totalScans}</span>
        {/if}
      </button>
      <!-- span wrapper: FA dom.watch() replaces bare <i>; a top-level <i> in a
           conditional block crashes Svelte teardown on unmount -->
      {#if i < STEPS.length - 1}<span><i class="fas fa-chevron-right text-muted small" /></span>{/if}
    {/each}
  </div>

  {#if step === 0}
    <div class="card" style="max-width: 40rem">
      <div class="card-body">
        <h5 class="card-title">What are you counting?</h5>
        <p class="text-muted small">
          Scope is the branch + equipment type you physically counted. Everything on hand in this
          scope that you <em>don't</em> scan will be listed as missing.
        </p>
        <ScopePicker stacked showMissAction {branches} {unitTypes} {missActions} />
        {#if $fleet.status === "loading"}
          <div class="text-muted small mt-2">
            <span><i class="fas fa-spinner fa-spin me-1" /></span>Loading the units in this scope…
          </div>
        {:else if $fleet.status === "error"}
          <div class="alert alert-danger small py-2 mt-2 mb-0">
            <span><i class="fas fa-exclamation-triangle me-1" /></span>{$fleet.error}
            <button class="btn btn-sm btn-link py-0" on:click={() => ensureFleet(scopeUnitsUrl, $scope)}>
              Try again
            </button>
          </div>
        {:else if scopeReady}
          <div class="text-muted small mt-2">
            Expected on hand in this scope: <strong>{stats.expectedCount}</strong> units
          </div>
        {:else}
          <div class="text-muted small mt-2">Pick a branch and an equipment type to continue.</div>
        {/if}
      </div>
    </div>
  {:else if step === 1}
    <div class="card" style="max-width: 40rem">
      <div class="card-body">
        <h5 class="card-title">Add your scan lists</h5>
        <p class="text-muted small">
          Paste or upload each list from the count. Lists accumulate — add as many as you need.
        </p>
        <PasteBox />
      </div>
    </div>
  {:else if step === 2 && building}
    <!-- the frame we bought in goToStep(): this is all the browser paints while
         the review tree is being built (spec-addendum §D) -->
    <div class="recon-building" role="status">
      <span class="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
      <span>Building review…</span>
    </div>
  {:else if step === 2}
    {#if stats.misScope}
      <div class="alert alert-warning">
        <span><i class="fas fa-exclamation-triangle me-2" /></span>
        <strong>{Math.round(stats.outsideShare * 100)}%</strong> of your matched scans belong to a
        different branch or type than the selected scope. Check the scope on step 1 before trusting
        the missing list.
      </div>
    {/if}

    <!-- summary strip: how the count went, before the decision work -->
    <div class="recon-summary card mb-3">
      <div class="recon-stat">
        <span class="recon-stat-icon text-success"><i class="fas fa-check" /></span>
        <span class="recon-stat-num">{groups.matched.length}</span>
        <span class="recon-stat-label">Matched &amp; accounted for<small>present and on hand — no change</small></span>
      </div>
      <div class="recon-stat">
        <span class="recon-stat-icon text-info"><i class="fas fa-skiing" /></span>
        <span class="recon-stat-num">{groups.onRental.length}</span>
        <span class="recon-stat-label">Out on Rental<small>currently rented — skipped</small></span>
      </div>
      <div
        class="recon-stat"
        title={groups.unmatched.length > 0
          ? `e.g. ${groups.unmatched.slice(0, 5).map((s) => s.value).join(", ")}`
          : ""}
      >
        <span class="recon-stat-icon text-muted"><i class="fas fa-ghost" /></span>
        <span class="recon-stat-num">{groups.unmatched.length}</span>
        <span class="recon-stat-label">Unmatched scans<small>kept on record, no action</small></span>
      </div>
    </div>

    <p class="text-muted small mb-3">
      Work through each group below and decide every row. Rows left undecided are
      <strong>not changed</strong> — nothing is written without an explicit choice.
    </p>

    <!-- needs review -->
    {#if nrPending > 0 || nrResolved > 0}
      <div class="recon-group card mb-3" style="--accent: #f59e0b; --accent-bg: #fef3c7">
        <button class="recon-group-header" class:done={doneStates["g:needsReview"]} on:click={() => toggle("needsReview")}>
          <span class="recon-chip"><i class="fas fa-balance-scale" /></span>
          <span class="recon-group-titles">
            <span class="recon-group-title">
              Needs Review <span class="recon-count">{nrPending + nrResolved}</span>
            </span>
            <span class="recon-group-sub">ambiguous scans — pick the right unit</span>
          </span>
          <Tally parts={nrParts} done={doneStates["g:needsReview"]} />
          <span class="recon-chevron" class:open={open.needsReview}><i class="fas fa-chevron-down" /></span>
        </button>
        {#if open.needsReview}
          <div class="card-body pt-3">
            {#each groups.needsReview as scan (scan.value)}
              <NeedsReviewPicker {scan} />
            {/each}
            <!-- tombstones: resolved picks stay put, undo-able -->
            {#each groups.reviewResolved as t (t.value)}
              <div class="recon-tombstone">
                <span class="ts-check"><i class="fas fa-check-circle" /></span>
                <span class="recon-id">
                  <i class="fas fa-{t.kind === 'sku' ? 'tag' : 'barcode'}" />
                  <span class="font-monospace">{t.value}</span>
                </span>
                {#if t.pick === "none"}
                  <span class="small text-muted">no match — scan kept on record, not tied to a unit</span>
                {:else}
                  <span class="small text-muted">
                    resolved to <strong>{t.unit.itemName}</strong> · {t.unit.sku} → {t.groupLabel}
                  </span>
                {/if}
                <button class="btn btn-sm btn-link ms-auto py-0" on:click={() => pickReview(t.value, undefined)}>
                  Undo
                </button>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    <!-- reactivations -->
    {#if groups.reactivations.length > 0}
      <div class="recon-group card mb-3" style="--accent: #198754; --accent-bg: #d1e7dd">
        <button class="recon-group-header" class:done={doneStates["g:reactivations"]} on:click={() => toggle("reactivations")}>
          <span class="recon-chip"><i class="fas fa-undo" /></span>
          <span class="recon-group-titles">
            <span class="recon-group-title">
              Scanned but Marked Gone <span class="recon-count">{groups.reactivations.length}</span>
            </span>
            <span class="recon-group-sub">on the rack, but their status says they left</span>
          </span>
          <Tally parts={tallyParts(reactCounts)} done={doneStates["g:reactivations"]} />
          <span class="recon-chevron" class:open={open.reactivations}><i class="fas fa-chevron-down" /></span>
        </button>
        <!-- outside the collapse on purpose: deciding in bulk must never require
             mounting the rows first (spec-addendum §D) -->
        <div class="recon-toolbar">
          <span class="text-muted small">
            {#if groups.reactivations.length >= 2}Decide each row, or in bulk:{:else}Decide the row below.{/if}
          </span>
          <!-- bulk status buttons need at least two rows to be worth the clutter
               (spec-addendum §E) -->
          {#if groups.reactivations.length >= 2}
            <button class="btn btn-sm btn-outline-success ms-auto" on:click={() => decide(ids(groups.reactivations), "adjust")}>
              Set all as Active
            </button>
            <button class="btn btn-sm btn-outline-secondary" on:click={() => decide(ids(groups.reactivations), "skip")}>
              Do not adjust any
            </button>
          {/if}
        </div>
        {#if open.reactivations}
          <ul class="list-group list-group-flush">
            {#each groups.reactivations as row (row.unit.id)}
              <li
                class="list-group-item recon-row"
                class:recon-adjust={$decisions[row.unit.id] === "adjust"}
                class:recon-skip={$decisions[row.unit.id] === "skip"}
              >
                <span class="recon-ids">
                  {#if row.unit.barcode}
                    <span class="recon-id"><i class="fas fa-barcode" /> <span class="font-monospace">{row.unit.barcode}</span></span>
                  {/if}
                  {#if row.unit.sku}
                    <span class="recon-id"><i class="fas fa-tag" /> <span class="font-monospace">{row.unit.sku}</span></span>
                  {/if}
                </span>
                <span class="flex-grow-1 small">
                  {row.unit.itemName}
                  <span class="text-muted">· Size {row.unit.size || "—"}</span>
                  {#if row.unit.rack}<span class="text-muted"> · Rack {row.unit.rack}</span>{/if}
                </span>
                <StatusTag status={row.unit.status} />
                <span class="recon-arrow"><i class="fas fa-arrow-right" /></span>
                <DecisionControl
                  decision={$decisions[row.unit.id] || null}
                  target={$overrides[row.unit.id] || "active"}
                  onDecide={(d) => decide([row.unit.id], d)}
                  onTarget={(s) => setOverride(row.unit.id, s)}
                />
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}

    <!-- misses -->
    {#if groups.misses.length > 0}
      <div class="recon-group card mb-3" style="--accent: #dc3545; --accent-bg: #f8d7da">
        <button class="recon-group-header" class:done={doneStates["g:misses"]} on:click={() => toggle("misses")}>
          <span class="recon-chip"><i class="fas fa-question" /></span>
          <span class="recon-group-titles">
            <span class="recon-group-title">
              On Hand but Not Scanned <span class="recon-count">{groups.misses.length}</span>
            </span>
            <span class="recon-group-sub">expected in this scope, missing from your count</span>
          </span>
          <Tally parts={tallyParts(missCounts)} done={doneStates["g:misses"]} />
          <span class="recon-chevron" class:open={open.misses}><i class="fas fa-chevron-down" /></span>
        </button>
        {#if open.misses}
          <div class="card-body">
            {#each missSubList as sg (sg.id)}
              <div
                class="recon-item-group"
                class:recon-item-info={sg.info}
                class:recon-item-done={sg.counts.undecided === 0}
                class:recon-item-collapsed={!subOpen[sg.id]}
              >
                <div
                  class="recon-item-header"
                  role="button"
                  tabindex="0"
                  on:click={() => toggleSub(sg.id)}
                  on:keydown={(e) => (e.key === "Enter" || e.key === " ") && toggleSub(sg.id)}
                >
                  {#if sg.counts.undecided === 0}
                    <span class="recon-sub-check"><i class="fas fa-check-circle" /></span>
                  {/if}
                  <strong class="small">{sg.title}</strong>
                  <span class="badge {sg.badgeClass} ms-1">{sg.rows.length}</span>
                  {#if sg.hint && sg.counts.undecided > 0}
                    <span class="text-muted small ms-2">{sg.hint}</span>
                  {/if}
                  <Tally parts={tallyParts(sg.counts)} done={sg.counts.undecided === 0} />
                  <!-- bulk decisions stay in the header, collapsed or not: they
                       must never require mounting the rows (spec-addendum §D) —
                       but only where there are ≥ 2 rows to act on (spec-addendum §E) -->
                  {#if sg.rows.length >= 2}
                    {#if missTarget}
                      <button class="btn btn-sm btn-outline-success ms-2" on:click|stopPropagation={() => decide(ids(sg.rows), "adjust")}>
                        Set all as {statusLabel(missTarget)}
                      </button>
                    {/if}
                    <button class="btn btn-sm btn-outline-secondary ms-2" on:click|stopPropagation={() => decide(ids(sg.rows), "skip")}>
                      Do not adjust
                    </button>
                  {/if}
                  <span class="recon-chevron recon-chevron-sm" class:open={subOpen[sg.id]}><i class="fas fa-chevron-down" /></span>
                </div>
                {#if subOpen[sg.id]}
                  <ul class="list-group list-group-flush">
                    {#each sg.rows as row (row.unit.id)}
                      <li
                        class="list-group-item recon-row py-1"
                        class:recon-adjust={$decisions[row.unit.id] === "adjust"}
                        class:recon-skip={$decisions[row.unit.id] === "skip"}
                      >
                        <span class="recon-ids">
                          {#if row.unit.barcode}
                            <span class="recon-id"><i class="fas fa-barcode" /> <span class="font-monospace">{row.unit.barcode}</span></span>
                          {/if}
                          {#if row.unit.sku}
                            <span class="recon-id"><i class="fas fa-tag" /> <span class="font-monospace">{row.unit.sku}</span></span>
                          {/if}
                        </span>
                        {#if sg.itemGroup}
                          <span class="flex-grow-1 small text-muted">
                            Size {row.unit.size || "—"}
                            {#if row.unit.rack}<span> · Rack {row.unit.rack}</span>{/if}
                          </span>
                        {:else}
                          <span class="flex-grow-1 small">
                            {row.unit.itemName}
                            <span class="text-muted">· Size {row.unit.size || "—"}</span>
                            {#if row.unit.rack}<span class="text-muted"> · Rack {row.unit.rack}</span>{/if}
                          </span>
                        {/if}
                        <StatusTag status={row.unit.status} />
                        <span class="recon-arrow"><i class="fas fa-arrow-right" /></span>
                        <DecisionControl
                          decision={$decisions[row.unit.id] || null}
                          target={$overrides[row.unit.id] || missTarget}
                          onDecide={(d) => decide([row.unit.id], d)}
                          onTarget={(s) => setOverride(row.unit.id, s)}
                        />
                      </li>
                    {/each}
                  </ul>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    <!-- outside scope: visually unmistakable (tinted header, strong accent) -->
    {#if groups.outsideScope.length > 0}
      <div class="recon-group recon-group-alert card mb-3" style="--accent: #b02a37; --accent-bg: #f8d7da">
        <button class="recon-group-header" class:done={doneStates["g:outsideScope"]} on:click={() => toggle("outsideScope")}>
          <span class="recon-chip"><i class="fas fa-crosshairs" /></span>
          <span class="recon-group-titles">
            <span class="recon-group-title text-danger">
              Scanned Here, but Belongs Elsewhere <span class="recon-count">{groups.outsideScope.length}</span>
            </span>
            <span class="recon-group-sub">
              another branch or equipment type — transfer wrong-branch units here, adjust statuses,
              or both
            </span>
          </span>
          <Tally parts={outsideParts} done={doneStates["g:outsideScope"]} />
          <span class="recon-chevron" class:open={open.outsideScope}><i class="fas fa-chevron-down" /></span>
        </button>
        <!-- outside the collapse on purpose: the bulk transfer has to be usable
             without expanding a single row (spec-addendum §D) -->
        {#if outsideEligible.length > 0}
          <TransferBanner
            eligibleCount={outsideEligible.length}
            ineligibleCount={groups.outsideScope.length - outsideEligible.length}
            transferCount={outsideCounts.transfer}
            branchName={scopeBranchName}
            applied={bulkApplied}
            onTransferAll={(alsoActive) => transferAll(ids(outsideEligible), alsoActive)}
            onUndo={undoBulkTransfer}
          />
        {/if}
        <div class="recon-toolbar">
          <span class="text-muted small">
            These aren't part of this count, so no status is suggested. Choose one explicitly to
            change a unit, or Do not adjust to leave it as is.
          </span>
        </div>
        {#if open.outsideScope}
          <ul class="list-group list-group-flush">
            {#each groups.outsideScope as row (row.unit.id)}
              <li
                class="list-group-item recon-row"
                class:recon-adjust={$decisions[row.unit.id] === "adjust" && !$transfers[row.unit.id]}
                class:recon-transfer={$transfers[row.unit.id]}
                class:recon-skip={$decisions[row.unit.id] === "skip" && !$transfers[row.unit.id]}
              >
                <span class="recon-ids">
                  {#if row.unit.barcode}
                    <span class="recon-id"><i class="fas fa-barcode" /> <span class="font-monospace">{row.unit.barcode}</span></span>
                  {/if}
                  {#if row.unit.sku}
                    <span class="recon-id"><i class="fas fa-tag" /> <span class="font-monospace">{row.unit.sku}</span></span>
                  {/if}
                </span>
                <span class="flex-grow-1 small">
                  {row.unit.itemName}
                  <span class="badge bg-danger bg-opacity-75 ms-1">{row.unit.itemType}</span>
                </span>
                <!-- where the unit lives today: the whole point of the card, and
                     the only place a row has ever shown its branch (§C) -->
                <span class="recon-home" class:recon-home-here={!isTransferable(row.unit, $scope)}>
                  <i class="fas fa-home" />
                  {$branchNames[row.unit.branchId] || "Unknown branch"}
                </span>
                <StatusTag status={row.unit.status} />
                <span class="recon-arrow"><i class="fas fa-arrow-right" /></span>
                {#if isTransferable(row.unit, $scope)}
                  <TransferToggle
                    selected={Boolean($transfers[row.unit.id])}
                    onToggle={() => toggleTransfer(row.unit.id)}
                  />
                {/if}
                <DecisionControl
                  decision={$decisions[row.unit.id] || null}
                  target={$overrides[row.unit.id] || null}
                  onDecide={(d) => decide([row.unit.id], d)}
                  onTarget={(s) => setOverride(row.unit.id, s)}
                />
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}
  {:else}
    <div class="card" style="max-width: 44rem">
      <div class="card-body">
        <h5 class="card-title">Apply this reconciliation?</h5>
        <p class="small text-muted">
          {stats.totalScans} scans · {stats.resolvedCount} units identified ·
          {skipTotal} kept as is · {nrPending} still ambiguous
        </p>
        {#if undecidedTotal > 0}
          <div class="alert alert-warning small py-2">
            <span><i class="fas fa-info-circle me-1" /></span>
            <strong>{undecidedTotal} rows were left undecided.</strong> Undecided rows default to
            no adjustment — their statuses stay exactly as they are. If that's what you intended,
            apply away; otherwise go back to Review and decide them (the bulk buttons work too).
          </div>
        {/if}
        {#if summary.transferCount > 0}
          <p class="recon-transfer-line small">
            <span><i class="fas fa-exchange-alt me-1" /></span>
            <strong>{summary.transferCount} units</strong>
            transferred to <strong>{scopeBranchName}</strong>
            ({summary.transferActiveCount} set to Active{#if summary.transferCount > summary.transferActiveCount},
              {summary.transferCount - summary.transferActiveCount} with another or kept status{/if})
          </p>
        {/if}
        <table class="table table-sm w-auto">
          <tbody>
            {#each Object.entries(summary.byTransition) as [transition, count]}
              <tr><td>{transition}</td><td class="text-end fw-bold">{count}</td></tr>
            {/each}
            <tr class="table-light"><td>total status changes</td><td class="text-end fw-bold">{summary.total}</td></tr>
          </tbody>
        </table>
        {#if nrPending > 0}
          <div class="alert alert-warning small py-2">
            {nrPending} ambiguous scans are unresolved — they'll be recorded with no action.
          </div>
        {/if}
        {#if confirmErrors.length > 0}
          <div class="alert alert-danger small py-2">
            <span><i class="fas fa-exclamation-triangle me-1" /></span>
            <strong>Nothing was applied.</strong> Your review is exactly as you left it — fix this
            and apply again.
            <ul class="mb-0 mt-1">
              {#each confirmErrors as message}
                <li>{message}</li>
              {/each}
            </ul>
          </div>
        {/if}
        <button class="btn btn-primary" disabled={applying} on:click={apply}>
          {#if applying}
            <span><i class="fas fa-spinner fa-spin me-1" /></span>Applying…
          {:else}
            <!-- the space belongs INSIDE the block: Svelte trims whitespace at a
                 block's edges, so a leading newline here is swallowed and the
                 button reads "…status changesand 2 transfers" -->
            Apply {summary.total} status changes{#if summary.transferCount > 0}{" and "}{summary.transferCount} transfers{/if}
          {/if}
        </button>
      </div>
    </div>
  {/if}

  <!-- nav: in the page, right under the step content -->
  <div class="d-flex align-items-center mt-4 pt-3 border-top" style={navWidth ? `max-width: ${navWidth}` : ""}>
    <button class="btn btn-outline-secondary" disabled={step === 0} on:click={() => goToStep(step - 1)}>
      <span><i class="fas fa-arrow-left me-1" /></span>Back
    </button>
    <div class="ms-auto d-flex align-items-center gap-3">
      {#if step >= 2}
        <span class="small text-muted">
          {$changes.length} status changes staged
          {#if outsideCounts.transfer > 0}
            · <span class="recon-staged-transfer">{outsideCounts.transfer} to transfer</span>
          {/if}
          {#if undecidedTotal > 0}
            · <span class="recon-undecided">{undecidedTotal} undecided</span>
          {/if}
        </span>
      {/if}
      <button
        class="btn btn-primary"
        disabled={step === STEPS.length - 1 ||
          (step === 0 && !scopeReady) ||
          (step === 1 && $entries.length === 0)}
        on:click={() => goToStep(step + 1)}
      >
        Next<span><i class="fas fa-arrow-right ms-1" /></span>
      </button>
    </div>
  </div>
</div>

<style>
  /* the one-frame "Building review…" placeholder */
  .recon-building {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 3rem 0;
    color: #6c757d;
  }

  /* summary strip */
  .recon-summary {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
    padding: 0.5rem 0;
  }
  .recon-stat {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.25rem 1.25rem;
    flex: 1 1 0;
    min-width: 14rem;
  }
  .recon-stat + .recon-stat {
    border-left: 1px solid #e9ecef;
  }
  .recon-stat-num {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
  }
  .recon-stat-label {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.2;
  }
  .recon-stat-label small {
    font-weight: 400;
    color: #6c757d;
  }

  /* group cards */
  .recon-group {
    border: 1px solid #e3e6ea;
    border-left: 4px solid var(--accent);
    overflow: hidden;
  }
  .recon-group-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    background: #fff;
    border: 0;
    padding: 0.65rem 1rem;
    text-align: left;
    cursor: pointer;
  }
  .recon-group-header:hover {
    background: #f8f9fa;
  }
  /* done-trail: a finished card reads calm and green-tinged */
  .recon-group-header.done {
    background: #f6fbf8;
  }
  .recon-group-alert .recon-group-header {
    background: #fdf0f1;
  }
  .recon-group-alert .recon-group-header.done {
    background: #f6fbf8;
  }
  .recon-chip {
    width: 2.1rem;
    height: 2.1rem;
    flex: 0 0 auto;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--accent-bg);
    color: var(--accent);
  }
  .recon-group-titles {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
  }
  .recon-group-title {
    font-weight: 700;
  }
  .recon-count {
    display: inline-block;
    margin-left: 0.25rem;
    padding: 0.05rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    background: var(--accent-bg);
    color: var(--accent);
  }
  .recon-group-sub {
    font-size: 0.8rem;
    color: #6c757d;
  }
  .recon-chevron {
    margin-left: 0.75rem;
    color: #adb5bd;
    transition: transform 0.15s;
  }
  .recon-chevron.open {
    transform: rotate(180deg);
  }
  .recon-chevron-sm {
    font-size: 0.75rem;
  }
  .recon-toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-top: 1px solid #eceff2;
    border-bottom: 1px solid #eceff2;
    background: #fff;
  }

  /* item subgroups inside misses */
  .recon-item-group {
    border: 1px solid #e3e6ea;
    border-radius: 0.375rem;
    margin-bottom: 0.75rem;
    overflow: hidden;
  }
  .recon-item-header {
    display: flex;
    align-items: center;
    padding: 0.45rem 0.75rem;
    background: #f8f9fa;
    border-bottom: 1px solid #eceff2;
    cursor: pointer;
  }
  .recon-item-header:hover {
    background: #f1f3f5;
  }
  .recon-item-info .recon-item-header {
    background: #e7f6fb;
  }
  .recon-item-done .recon-item-header {
    background: #f6fbf8;
    border-bottom-color: transparent;
  }
  /* nothing below it to divide from */
  .recon-item-collapsed .recon-item-header {
    border-bottom-color: transparent;
  }
  .recon-sub-check {
    color: #198754;
    margin-right: 0.4rem;
  }

  /* needs-review tombstones */
  .recon-tombstone {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.35rem 0.75rem;
    margin-bottom: 0.35rem;
    border: 1px solid #e3e6ea;
    border-radius: 0.375rem;
    background: #f6fbf8;
  }
  .ts-check {
    color: #198754;
  }

  /* rows */
  .recon-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .recon-row.recon-adjust {
    background: #f2faf6;
  }
  /* transfer reads blue throughout, and outranks the status tint: moving branch
     is the bigger fact about the row (spec-addendum §C) */
  .recon-row.recon-transfer {
    background: #f2f7fd;
  }
  .recon-row.recon-skip {
    opacity: 0.55;
  }
  /* home branch of the unit as it stands today */
  .recon-home {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    flex: 0 0 auto;
    font-size: 0.8rem;
    font-weight: 600;
    color: #0b5ed7;
    white-space: nowrap;
  }
  /* already homed here: informational, not something to act on */
  .recon-home.recon-home-here {
    color: #6c757d;
    font-weight: 400;
  }
  .recon-home :global(svg),
  .recon-home i {
    color: inherit;
    opacity: 0.7;
  }
  .recon-transfer-line {
    color: #0b5ed7;
  }
  .recon-staged-transfer {
    color: #0b5ed7;
    font-weight: 600;
  }
  .recon-ids {
    display: flex;
    gap: 0.75rem;
    flex: 0 0 16rem;
    font-size: 0.85rem;
  }
  .recon-id {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    white-space: nowrap;
  }
  .recon-id :global(svg),
  .recon-id i {
    color: #adb5bd;
  }
  .recon-arrow {
    color: #adb5bd;
    font-size: 0.8rem;
  }
  .recon-undecided {
    color: #b45309;
    font-weight: 600;
  }
</style>
